import React, { useState } from "react";
import { Box, Tab, Tabs, Typography, Chip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FeatureGate } from "../../components/FeatureGate/FeatureGate";
import { Sidebar } from "../../containers/Sidebar/Sidebar";
import { getCurrentFleetId } from "../../utils/getCurrentFleet";
import { useTaxReadiness } from "../../api/fiscalite";
import { Synthese } from "./tabs/Synthese";
import { Completude } from "./tabs/Completude";
import { Documents } from "./tabs/Documents";
import { Opportunites } from "./tabs/Opportunites";

/**
 * Optimisation fiscale.
 *
 * Tab order is a product decision, not an alphabetical accident: COMPLETUDE
 * comes before the totals whenever the fleet is not ready. A confident total
 * computed on half the vehicles is the fastest way to lose a customer's trust
 * in a tax product, so when data is missing the page opens on the gap.
 */
export const Fiscalite: React.FC = () => {
  const { t } = useTranslation();
  const fleetId = getCurrentFleetId();

  // Declaration happens in January for the previous calendar year, so that is
  // the year a user opening this page in Q1 actually wants to see.
  const defaultYear = new Date().getMonth() === 0
    ? new Date().getFullYear() - 1
    : new Date().getFullYear();

  const [fiscalYear, setFiscalYear] = useState<number>(defaultYear);
  const { readiness } = useTaxReadiness(fleetId, fiscalYear);

  const notReady = !!readiness && readiness.readiness_ratio < 1;
  const [tab, setTab] = useState<number>(0);
  const [tabInitialised, setTabInitialised] = useState(false);

  // Land on Complétude the first time we learn the fleet is incomplete.
  React.useEffect(() => {
    if (!tabInitialised && readiness) {
      setTabInitialised(true);
      if (readiness.readiness_ratio < 1) setTab(1);
    }
  }, [readiness, tabInitialised]);

  const years = Array.from({ length: 4 }, (_, i) => defaultYear - i);

  return (
    // Sidebar OUTSIDE FeatureGate on purpose: when the plan does not include
    // the feature, the gate blurs its children and swallows clicks. Wrapping
    // the nav in it too would leave the user with no way out of the page.
    // Sidebar's content area is `overflow: hidden`, so the scroll container is
    // ours.
    <Sidebar>
      <Box sx={{ height: "100%", overflowY: "auto" }}>
        <FeatureGate feature="tax_optimization">
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {t("tax.title", "Optimisation fiscale")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t(
                "tax.subtitle",
                "Taxes annuelles, amortissements, TVA et avantages en nature — chaque montant est justifiable."
              )}
            </Typography>
          </Box>

          <Tabs
            value={years.indexOf(fiscalYear)}
            onChange={(_, index) => setFiscalYear(years[index])}
            sx={{ minHeight: 36 }}
          >
            {years.map((year) => (
              <Tab key={year} label={year} sx={{ minHeight: 36, minWidth: 72 }} />
            ))}
          </Tabs>
        </Box>

        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          sx={{ mt: 2, borderBottom: 1, borderColor: "divider" }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label={t("tax.tabs.summary", "Synthèse")} />
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {t("tax.tabs.readiness", "Complétude")}
                {/* Count vehicles that cannot be assessed, not gap categories:
                    a fleet with no fiscal profile at all has zero gap rows and
                    zero computable vehicles, and a "0" badge there reads as
                    "nothing to do". */}
                {notReady && (
                  <Chip
                    size="small"
                    color="warning"
                    label={(readiness?.vehicles_total ?? 0) - (readiness?.vehicles_ready ?? 0)}
                  />
                )}
              </Box>
            }
          />
          <Tab label={t("tax.tabs.documents", "Documents")} />
          <Tab label={t("tax.tabs.opportunities", "Opportunités")} />
        </Tabs>

        <Box sx={{ mt: 3 }}>
          {tab === 0 && <Synthese fleetId={fleetId} fiscalYear={fiscalYear} onGoToReadiness={() => setTab(1)} />}
          {tab === 1 && <Completude fleetId={fleetId} fiscalYear={fiscalYear} onGoToDocuments={() => setTab(2)} />}
          {tab === 2 && <Documents fleetId={fleetId} />}
          {tab === 3 && <Opportunites fleetId={fleetId} />}
        </Box>
      </Box>
        </FeatureGate>
      </Box>
    </Sidebar>
  );
};

export default Fiscalite;
